import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const statusStyles = {
  Concept: "text-white/60 border-white/20",
  "In sviluppo": "text-accent border-accent/50",
  Alpha: "text-accent border-accent/50",
  "Pre-Order Soon": "text-black bg-accent border-accent",
};

export default function GamesListPage({ data }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section
      data-testid="games-list-page"
      className="relative bg-[#1A1A1F] pt-40 md:pt-48 pb-24 md:pb-40 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Page intro */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end"
        >
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                Vol. 01 — Catalogo
              </span>
            </div>
            <h1
              data-testid="games-list-title"
              className="font-heading uppercase text-white text-6xl md:text-8xl lg:text-[9rem] leading-[0.85]"
            >
              Giochi &<br />Progetti
            </h1>
          </div>
          <div className="lg:col-span-4 font-body text-sm md:text-base text-white/55 leading-relaxed max-w-md">
            L'intera line-up delle produzioni Ashvolt Studios — pubblicate, in sviluppo o in fase di concept. Ogni progetto è ideato, diretto e pubblicato interamente in-house.
          </div>
        </motion.div>

        {/* Editorial list */}
        <div className="border-t border-white/10">
          {data.releases.map((g, i) => (
            <motion.div
              key={g.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`games-list-item-${g.id}`}
            >
              <Link
                to={`/games/${g.id}`}
                className="group relative grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 border-b border-white/10 py-10 md:py-14 items-center hover:bg-white/[0.015] transition-colors duration-500"
              >
                <div className="lg:col-span-1 font-heading text-4xl md:text-5xl text-white/25 group-hover:text-accent transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="lg:col-span-3 relative overflow-hidden aspect-[4/3]">
                  <img
                    src={g.cover}
                    alt={g.title}
                    className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40">
                      {g.codename}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-white/25" />
                    <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/50">
                      {g.window}
                    </span>
                  </div>
                  <h2 className="font-heading uppercase text-4xl md:text-5xl lg:text-6xl text-white leading-none mb-4 group-hover:text-accent transition-colors duration-500">
                    {g.title}
                  </h2>
                  <p className="font-body text-sm md:text-base text-white/55 leading-relaxed max-w-lg">
                    {g.summary}
                  </p>
                </div>
                <div className="lg:col-span-2 flex flex-col items-start gap-3">
                  <span
                    className={`font-body text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border ${statusStyles[g.status] || "text-white/60 border-white/20"}`}
                  >
                    {g.status}
                  </span>
                  <div className="flex items-center gap-2">
                    {g.platforms.map((p) => (
                      <span
                        key={p}
                        className="font-body text-[10px] uppercase tracking-[0.25em] text-white/70 border border-white/15 px-2 py-1"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-1 flex justify-end">
                  <ArrowUpRight
                    size={32}
                    className="text-white/40 transition-all duration-500 group-hover:text-accent group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
